import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import ClientInfoForm from '../ClientInfoForm';
import EditCancelModal from '../Global/EditCancelModal';
import AddressDropdown from '../AddressDropdown'; // Importamos el componente AddressDropdown
import { updateClient, createClient, createClientAddress, logError, getClientById, updateClientAddress, searchClients, createLeadRegistry, patchLead } from '../../api';
import AddAddressModal from '../AddAddressModal'; // Importamos el modal de agregar dirección
import ClientSearchModal from '../Global/ClientSearchModal';
import { getSalesRepId, isPropietarioOrAdminOrGestion, getSalesRepId as getAuthSalesRepId } from '../../services/AuthService';

const OrderClientForm = ({
    onClientSelect,
    client,
    setClient,
    setIsDirty,
    handleGoBack,
    isDirty,
    setSelectedAddress,
    setSelectedBillingAddress,
    leadData
}) => {
    // Definimos el estado para la dirección seleccionada
    const [selectedShippingAddress, setSelectedShippingAddress] = useState(null);
    const [selectedBillingAddressInternal, setSelectedBillingAddressInternal] = useState(null);
    const [isBillingAddressSame, setIsBillingAddressSame] = useState(true); // Estado para el checkbox

    const [isEditing, setIsEditing] = useState(false);
    const [originalClient, setOriginalClient] = useState(null);
    const [isCreatingNewClient, setIsCreatingNewClient] = useState(!client && !leadData); // Solo crear nuevo por defecto si no hay cliente Y no viene de lead
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [searchingClient, setSearchingClient] = useState(false); // Cambiado para no buscar por defecto
    const [showAddAddressModal, setShowAddAddressModal] = useState(false); // Estado para mostrar el modal de dirección
    const [showClientModal, setShowClientModal] = useState(false);
    const currentSalesRepId = Number(getSalesRepId());

    // Buscar cliente automáticamente si hay datos del lead
    useEffect(() => {
        if (leadData && !client) {
            if (leadData.clientId) {
                // Si el lead ya tiene clientId, cargar ese cliente
                loadClientById(leadData.clientId);
            } else if (leadData.phone && !isCreatingNewClient) {
                // Si no tiene clientId, buscar por teléfono
                searchClientByPhone(leadData.phone);
            }
        }
    }, [leadData, client, isCreatingNewClient]);

    const loadClientById = async (clientId) => {
        try {
            const clientData = await getClientById(clientId);
            handleClientSelect(clientData);
        } catch (error) {
            console.error('Error cargando cliente del lead:', error);
            // Si falla, intentar buscar por teléfono como fallback
            if (leadData.phone) {
                searchClientByPhone(leadData.phone);
            }
        }
    };

    const searchClientByPhone = async (phone) => {
        try {
            const searchRequest = {
                phone: phone
            };
            const response = await searchClients(searchRequest, 1, 10);
            if (response.data && response.data.length > 0) {
                // Si encuentra un cliente, seleccionarlo automáticamente
                const foundClient = response.data[0];
                handleClientSelect(foundClient);
            } else {
                // Si no encuentra cliente, prellenar datos para crear uno nuevo
                createNewClientWithLeadData();
            }
        } catch (error) {
            console.error('Error buscando cliente por teléfono:', error);
            // En caso de error, prellenar para crear nuevo cliente
            createNewClientWithLeadData();
        }
    };

    const createNewClientWithLeadData = () => {
        setIsCreatingNewClient(true);
        setIsEditing(true);
        setSearchingClient(false);
        
        // Crear objeto cliente con datos del lead
        const newClientData = {
            id: null,
            name: leadData.name || '',
            lastName1: '', // El lead no tiene apellidos separados
            lastName2: '',
            phone: leadData.phone || '',
            phone2: '',
            email: '',
            dni: '',
            dateOfBirth: '',
            zipCode: leadData.zipCode || '',
            city: '',
            address: '',
            district: '',
            state: '',
            country: 'España',
            addresses: []
        };
        
        setClient(newClientData);
        setOriginalClient(null);
        setIsDirty(true);
    };

    // Este useEffect se ejecuta cuando el cliente cambia
    useEffect(() => {
        if (client && Array.isArray(client.addresses) && client.addresses.length > 0) {
            // Si no hay dirección seleccionada, seleccionamos la predeterminada o la primera
            if (!selectedShippingAddress) {
                const defaultAddress = client.addresses.find(address => address.isDefault);
                const initialAddress = defaultAddress || client.addresses[0];
                setSelectedShippingAddress(initialAddress); // Asignamos la dirección de envío
                setSelectedBillingAddress(initialAddress); // Asignamos la dirección de facturación
                setSelectedAddress(initialAddress); // Asignamos la dirección seleccionada
            }
        }
    }, [client, selectedShippingAddress, setSelectedAddress]); // Dependencias en client y selectedShippingAddress

    const handleClientSelect = (clientData) => {
        setClient(clientData);
        setShowClientModal(false);
        setOriginalClient({ ...clientData });
        setSearchingClient(false);
        setIsCreatingNewClient(false); // Ya no estamos creando nuevo cliente
        setIsEditing(false); // No estamos editando
        setIsDirty(true);
        onClientSelect && onClientSelect(clientData);
    };

    const fetchClient = async (clientId) => {
        try {
            const response = await getClientById(clientId);
            return response.data;
        } catch (err) {
            logError(err);
            console.error('Error al recargar cliente:', err);
            return null;
        }
    };

    const handleCreateNewClient = () => {
        setIsCreatingNewClient(true);
        setClient(null);
        setSearchingClient(false);
        setIsEditing(true);
        setOriginalClient(null);
    };

    const handleEditClient = () => setIsEditing(true);

    const handleCancelEdit = () => {
        if (isDirty) {
            setShowCancelModal(true);
        } else {
            cancelEdit();
        }
    };

    const handleCancelCreate = () => {
        if (isDirty) {
            setShowCancelModal(true);
        } else {
            cancelCreate();
        }
    };

    const handleSaveClientCreate = async (updatedClient, newAddress) => {
        try {
            let response;
    
            if (updatedClient.id) {
                response = await updateClient(updatedClient);
            } else {
                const { id, address, zipCode, city, district, state, country, ...clientWithoutId } = updatedClient;
                
                // Crear cliente
                response = await createClient(clientWithoutId);
                
                // Si hay datos de dirección, crear la dirección
                if (newAddress) {
                    const addressData = {
                        address: newAddress.address,
                        zipCode: newAddress.zipCode,
                        city: newAddress.city,
                        district: newAddress.district || '',
                        state: newAddress.state,
                        country: newAddress.country,
                        isDefault: true,
                        clientId: response.data.id,
                        ...(newAddress.latitude  && { latitude:  newAddress.latitude  }),
                        ...(newAddress.longitude && { longitude: newAddress.longitude }),
                        ...(newAddress.exactGeoMatch && { exactGeoMatch: newAddress.exactGeoMatch }),
                    };

                    const addressResponse = await createClientAddress(addressData);
                    response.data.addresses = [addressResponse.data];
                }
            }
    
            setClient(response.data);
            setOriginalClient({ ...response.data });
            setIsEditing(false);
            setIsCreatingNewClient(false);
            
            // Si viene de un lead, actualizar lead con clientId
            if (leadData && response.data.id) {
                try {
                    await patchLead(leadData.id, {
                        clientId: response.data.id
                    });
                } catch (leadError) {
                    console.error('Error actualizando lead:', leadError);
                }
            }
        } catch (error) {
            logError(error);
            console.error('Error guardando cliente:', error);
            alert('Hubo un error al guardar los cambios.');
        }
    };
    

    const handleSaveClientEdit = async (updatedClient, addressData) => {
        try {
            // 1. Guardar datos del cliente
            await updateClient(updatedClient.id, updatedClient);

            // 2. Guardar dirección
            const existingAddress = client.addresses?.find(addr => addr.isDefault);
            let updatedAddresses;

            if (existingAddress && existingAddress.id) {
                // Actualizar la dirección existente
                await updateClientAddress(existingAddress.id, {
                    ...addressData,
                    clientId: updatedClient.id,
                    id: existingAddress.id
                });

                // Reemplazar la dirección actualizada en el array
                updatedAddresses = client.addresses.map(addr =>
                    addr.id === existingAddress.id
                        ? { ...addressData, id: existingAddress.id, clientId: updatedClient.id }
                        : addr
                );
            } else {
                // Crear nueva si no existe
                const createResponse = await createClientAddress({
                    ...addressData,
                    clientId: updatedClient.id,
                    isDefault: true
                });

                const newAddress = createResponse.data;

                updatedAddresses = [...(client.addresses || []), newAddress];
            }

            // 3. Actualizar estado local sin pedir al backend
            const mergedClient = {
                ...updatedClient,
                addresses: updatedAddresses
            };

            setClient(mergedClient);
            setOriginalClient({ ...mergedClient });

            // 4. Actualizar dropdowns
            const defaultAddress = updatedAddresses.find(a => a.isDefault) || updatedAddresses[0];
            setSelectedShippingAddress(defaultAddress);
            setSelectedBillingAddressInternal(defaultAddress);
            setSelectedAddress(defaultAddress);

            // 5. Salir del modo edición
            setIsEditing(false);

        } catch (error) {
            logError(error);
            console.error('Error updating client:', error);
            alert('Hubo un error al guardar los cambios.');
        }
    };


    const cancelEdit = () => {
        if (isCreatingNewClient) {
            setClient(null);
            setSearchingClient(true);
            setIsCreatingNewClient(false);
            setIsEditing(false);
        } else {
            setClient(originalClient);
        }
        setIsEditing(false);
        setShowCancelModal(false);
    };

    const cancelCreate = () => {
        setClient(null);
        setSearchingClient(true);
        setIsCreatingNewClient(false);
        setShowCancelModal(false);
        setIsEditing(false);
        setIsDirty(false);
    };

    const confirmCancel = () => {
        setShowCancelModal(false);
        if (isCreatingNewClient) {
            cancelCreate();
        } else {
            cancelEdit();
        }
    };

    const handleDeseleccionarCliente = () => {
        setClient(null);
        setIsCreatingNewClient(true); // Volver a crear nuevo cliente por defecto
        setSearchingClient(false);
        setIsEditing(false);
        setIsDirty(false);
        setSelectedShippingAddress(null);
        setSelectedBillingAddressInternal(null);
        setSelectedAddress(null);
        setSelectedBillingAddress(null);
    };

    const handleSelectShippingAddress = (address) => {
        setSelectedShippingAddress(address); // Actualiza la dirección de envío
        if (isBillingAddressSame) {
            handleSelectBillingAddress(address);
        }
        setSelectedAddress(address); // Actualiza la dirección seleccionada
    };

    const handleSelectBillingAddress = (address) => {
        setSelectedBillingAddress(address); 
        setSelectedBillingAddressInternal(address);
    };

    const handleAddAddress = (newAddress) => {
        let updatedAddresses = [...(client?.addresses || [])];

        // Si la nueva dirección es predeterminada, desmarcar cualquier dirección predeterminada anterior
        if (newAddress.isDefault) {
            updatedAddresses = updatedAddresses.map(address =>
                address.isDefault ? { ...address, isDefault: false } : address
            );
        }

        // Agregar la nueva dirección
        updatedAddresses.push(newAddress);

        setClient(prevClient => {
            const updatedClient = {
                ...prevClient,
                addresses: updatedAddresses,
            };

            // Seleccionar la nueva dirección en el dropdown correspondiente
            if (isBillingAddressSame) {
                setSelectedShippingAddress(newAddress); // Selecciona en el dropdown de envío
                setSelectedAddress(newAddress); // Actualiza la dirección seleccionada
            } else {
                setSelectedBillingAddress(newAddress); // Selecciona en el dropdown de facturación
            }

            return updatedClient;
        });

        setShowAddAddressModal(false);
    };

    return (
        <div className="client-manager" data-testid="order-client-form">
            {/* Opción de seleccionar cliente - solo cuando no hay cliente seleccionado */}
            {!client && (
                <div className="mb-3">
                    <Button variant="primary" onClick={() => setShowClientModal(true)} data-testid="select-existing-client-button">
                        Seleccionar Cliente
                    </Button>
                </div>
            )}

            <ClientSearchModal
                show={showClientModal}
                onHide={() => setShowClientModal(false)}
                onClientSelect={handleClientSelect}
                excludeDisabled={true}
            />

            {/* Botones cuando hay cliente seleccionado */}
            {client && !isCreatingNewClient && !isEditing && (
                <div className="buttons mb-3" data-testid="client-action-buttons">
                   {(client?.salesRepId === currentSalesRepId || isPropietarioOrAdminOrGestion()) && (
                        <Button variant="primary" onClick={handleEditClient} className="me-2" data-testid="edit-client-button">
                            Editar Cliente
                        </Button>
                    )}

                    <Button variant="secondary" onClick={handleDeseleccionarCliente} data-testid="deselect-client-button">
                        Deseleccionar Cliente
                    </Button>
                </div>
            )}

            {/* Formulario de cliente - por defecto crear nuevo o mostrar existente */}
            {(isCreatingNewClient || client) ? (
                <ClientInfoForm
                    client={client || {}}
                    isEditing={isEditing || (isCreatingNewClient && !client)}
                    handleSubmit={isCreatingNewClient ? handleSaveClientCreate : handleSaveClientEdit}
                    handleCancel={isEditing && client && !isCreatingNewClient ? handleCancelEdit : null}
                    setIsDirty={setIsDirty}
                    showCancelButton={isEditing && client && !isCreatingNewClient} // Solo mostrar cancelar si estamos editando un cliente existente
                />
            ) : null}

            {!isCreatingNewClient && client && !isEditing && Array.isArray(client.addresses) && client.addresses.length > 0 && (
                <div>
                    {/* Dropdown para la dirección de envío */}
                    <div>
                        <label>Dirección de envío:</label>
                        <AddressDropdown
                            addresses={client.addresses || []}
                            selectedAddress={selectedShippingAddress || client.addresses[0]}
                            onSelectAddress={handleSelectShippingAddress}
                            id="shipping-address-dropdown"
                        />
                    </div>

                    {/* Checkbox para indicar si la dirección de facturación es la misma */}
                    <div>
                        <label>
                            <input
                                type="checkbox"
                                checked={isBillingAddressSame}
                                onChange={(e) => setIsBillingAddressSame(e.target.checked)}
                            />
                            Usar la misma dirección para facturación
                        </label>
                    </div>

                    {/* Dropdown para la dirección de facturación (solo si el checkbox no está marcado) */}
                    {!isBillingAddressSame && (
                        <div>
                            <label>Dirección de facturación:</label>
                            <AddressDropdown
                                addresses={client.addresses || []}
                                selectedAddress={selectedBillingAddressInternal || client.addresses[0]}
                                onSelectAddress={handleSelectBillingAddress}
                                id="billing-address-dropdown"
                            />
                            <p/>
                        </div>
                    )}
                </div>
            )}

            {!isCreatingNewClient && client && !isEditing && (client.salesRepId === currentSalesRepId || isPropietarioOrAdminOrGestion()) && (
                <div className="buttons">
                    <Button onClick={() => setShowAddAddressModal(true)} data-testid="add-address-button">
                        Agregar Nueva Dirección
                    </Button>
                </div>
            )}

            {showCancelModal && isDirty && (
                <EditCancelModal
                    show={showCancelModal}
                    onConfirm={confirmCancel}
                    onCancel={() => setShowCancelModal(false)}
                />
            )}

            <AddAddressModal
                show={showAddAddressModal}
                handleClose={() => setShowAddAddressModal(false)}
                handleSave={handleAddAddress}
                clientId={client?.id}
            />
        </div>
    );
};

export default OrderClientForm;